import { useState, useEffect, useCallback } from 'react';
import { plansApi, Plan, CreatePlanDto } from '../../../api/payments';
import { pricingVariantsApi, PricingVariant } from '../../../api/pricingVariants';
import { toast } from 'sonner';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UsePlansReturn } from '../types';
import { getErrorMessage } from '@/lib/errors';

const initialFormData: CreatePlanDto = {
  code: '',
  title: '',
  duration_value: 1,
  duration_unit: 'month',
  price: 0,
  currency: 'UZS',
  is_active: true,
  variant_id: null,
};

export function usePlans(): UsePlansReturn {
  const confirm = useConfirm();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [variants, setVariants] = useState<PricingVariant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // По умолчанию показываем все тарифы (вкл. неактивные) — иначе после деактивации
  // тариф пропадает из админки, и им нельзя управлять.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<CreatePlanDto>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await plansApi.getAll(includeInactive);
      setPlans(data);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить тарифы'));
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Список вариантов нужен, чтобы показать принадлежность тарифа и дать
  // перевесить его в другой вариант прямо из карточки тарифа.
  useEffect(() => {
    pricingVariantsApi
      .getAll()
      .then(setVariants)
      .catch(() => setVariants([]));
  }, []);

  const handleCreate = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await plansApi.create(formData);
      toast.success('Тариф создан');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchPlans();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось создать тариф'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Форма общая с редактированием: создание всегда начинается с чистых полей.
  const openCreateDialog = (open: boolean) => {
    if (open) setFormData(initialFormData);
    setIsCreateDialogOpen(open);
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      code: plan.code,
      title: plan.title,
      duration_value: plan.duration_value,
      duration_unit: plan.duration_unit,
      price: plan.price,
      old_price: plan.old_price || null,
      currency: plan.currency,
      is_active: plan.is_active,
      metadata: plan.metadata || undefined,
      variant_id: plan.variant_id ?? null,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPlan || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await plansApi.update(editingPlan.id, formData);
      toast.success('Тариф обновлён');
      setIsEditDialogOpen(false);
      setEditingPlan(null);
      fetchPlans();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось обновить тариф'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить тариф?',
      description: 'Это действие необратимо.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await plansApi.delete(id);
      toast.success('Тариф удалён');
      fetchPlans();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить тариф'));
    }
  };

  const handleToggleActive = async (plan: Plan, isActive: boolean) => {
    if (togglingId) return;
    setTogglingId(plan.id);
    // Оптимистично: переключатель меняется сразу, при ошибке откатываем.
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: isActive } : p)));
    try {
      const updated = await plansApi.update(plan.id, { is_active: isActive });
      // Без перезагрузки списка: строка остаётся на месте, даже если неактивные скрыты, —
      // чтобы можно было сразу вернуть переключатель.
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: updated?.is_active ?? isActive } : p)));
      toast.success(isActive ? `«${plan.title}» показан на экране оплаты` : `«${plan.title}» скрыт с экрана оплаты`);
    } catch (error: unknown) {
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: plan.is_active } : p)));
      toast.error(getErrorMessage(error, 'Не удалось изменить активность тарифа'));
    } finally {
      setTogglingId(null);
    }
  };

  return {
    handleToggleActive,
    togglingId,
    loadError,
    retryLoad: fetchPlans,
    plans,
    variants,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPlan,
    formData,
    isSubmitting,
    setIncludeInactive,
    setIsCreateDialogOpen: openCreateDialog,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  };
}
